import React from "react";
import { Button, Loader, Modal, Text, UnstyledButton } from "@mantine/core";
import BulkClientModalErrors from "./BulkClientModalErrors";
import UploadForm from "./BulkClientModalUploadForm";
import { useBulkUpload } from "./useBulkClientModal";
import { BulkClientModalAlert } from "./BulkClientModalAlert";
import { IconDownload } from "@tabler/icons-react";
import { downloadBulkClientUploadTemplate } from "@/pages/TaxProClientList/components/BulkClientModal/bulkClientModalUtil";

/* Magic Strings */
const MODAL_TITLE = "Bulk Import Clients";
const CLIENT_EMAIL_WARNING =
  "Imported clients will receive a welcome email as soon as they're added to your system.";
const MODAL_DESCRIPTION_REGULAR =
  "Import your clients at the same time. Make sure your CSV file has columns for ";
const MODAL_DESCRIPTION_STRONG =
  "first name, last name, email address, and phone number.";

type BulkModalProps = {
  opened: boolean;
  onClose: () => void;
};

/* Main Bulk Modal Component */
const BulkClientModal: React.FC<BulkModalProps> = ({ opened, onClose }) => {
  const {
    csv,
    notification,
    errorModalOpen,
    failedRecords,
    existingRecords,
    fileErrors,
    setCsv,
    setErrorModalOpen,
    handleBulkUpload,
    loading,
    resetState,
  } = useBulkUpload();

  const hasErrors = Boolean(failedRecords.length || fileErrors.length);
  const showDetails =
    !notification || (!loading && notification?.type !== "success");

  return (
    <Modal
      className="client-modal"
      opened={opened}
      centered
      onClose={() => {
        resetState();
        onClose();
      }}
      title={MODAL_TITLE}
      padding="lg"
      closeOnClickOutside={!loading}
      withCloseButton={!loading}
      closeOnEscape={!loading}
    >
      {/* Modal Description */}
      <Text c="dimmed" size="sm">
        {MODAL_DESCRIPTION_REGULAR}
        <strong>{MODAL_DESCRIPTION_STRONG}</strong>
      </Text>

      {/* Download Template*/}
      {showDetails ? (
        <Button
          mt="sm"
          variant="outline"
          leftSection={<IconDownload />}
          onClick={downloadBulkClientUploadTemplate}
          style={{ borderRadius: 10 }}
        >
          Download CSV Template
        </Button>
      ) : null}

      {/* Show loading icon*/}
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            margin: 20,
          }}
        >
          <Loader color="blue" />
        </div>
      ) : null}

      {/* Notification (used for success, warnings, errors) */}
      {notification && (
        <BulkClientModalAlert
          type={notification.type}
          message={notification.message}
        >
          {/* Create link to open errors. */}
          {hasErrors ? (
            <UnstyledButton
              style={{ fontSize: ".9rem", marginTop: 10 }}
              onClick={() => setErrorModalOpen(true)}
              component="strong"
            >
              See Details
            </UnstyledButton>
          ) : null}
        </BulkClientModalAlert>
      )}

      {/* Error Modal (shows records that failed to upload correctly) */}
      <BulkClientModalErrors
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        failedRecords={failedRecords}
        existingRecords={existingRecords}
        fileErrors={fileErrors}
      />

      {/* Upload Form (CSV Upload and Template Download) */}
      {showDetails ? (
        <UploadForm csv={csv} setCsv={setCsv} onSubmit={handleBulkUpload}>
          {/* Notice that newly created clients will be emailed upon creation. */}
          <BulkClientModalAlert type="notice" message={CLIENT_EMAIL_WARNING} />
        </UploadForm>
      ) : null}
    </Modal>
  );
};

export default BulkClientModal;
